import h from 'hyperscript';

import checkIcon from '@disy/cadenza-icons/check.svg';
import errorIcon from '@disy/cadenza-icons/error.svg';
import infoIcon from '@disy/cadenza-icons/info.svg';
import warningIcon from '@disy/cadenza-icons/warning.svg';
import { type StyleClass, addStyleClass } from '@disy/lib/add-style-class';
import { escapeAndMarkHTML } from '@disy/lib/escape-and-mark';
import { icon } from '@disy/lib/icon';

import type { ModalSize } from 'ui/dialog/modal/modal';
import { Modal } from 'ui/dialog/modal/modal';

import './alert-dialog.css';
import i18n from './alert-dialog.properties';

export type AlertDialogType = 'error' | 'info' | 'warning' | 'success';

export interface AlertDialogOptions {
  /** Alert dialog type */
  type?: AlertDialogType;
  /** Text for the submit button */
  submit?: string;
  /** Text for the alternative submit button */
  alternativeSubmit?: string;
  /** Whether cancel is allowed. If truthy, a cancel button is added. If string, given string is used as text of cancel button. */
  cancel?: boolean | string;
  /** Text to display while submitAction is in progress */
  loadingText?: string;
  /** Text to display while alternative submit is in progress */
  alternativeLoadingText?: string;
  /** On submit (confirm), AlertDialog executes function and sets loading state for returned promise. */
  submitCallback?: (alertDialog: AlertDialog) => void | boolean | Promise<unknown>;
  /** On alternative submit button, AlertDialog executes function and sets loading state for returned promise. */
  alternativeSubmitCallback?: (alertDialog: AlertDialog) => boolean | Promise<unknown>;
  size?: ModalSize;
  message?: string | Node;
  /** Optional additional css class to add to the dialog */
  styleClass?: StyleClass;
  shouldCloseOnLoadEnd?: boolean;
}

const ALERT_TYPE_TO_ICON: Record<AlertDialogType, string> = {
  error: errorIcon,
  warning: warningIcon,
  info: infoIcon,
  success: checkIcon,
};

/**
 * Create modal options rendering a title and title icon for the provided alert type.
 *
 * @param type
 * @return The modal options for the given alert type
 */
export function createAlertModalOptions(type: AlertDialogType) {
  return { title: i18n(`title.${type}`), titleIcon: icon(ALERT_TYPE_TO_ICON[type]) };
}

export class AlertDialog extends Modal<boolean> {
  #submitCallback;
  #alternativeSubmitCallback;
  #loadingText;
  #alternativeLoadingText;
  _shouldCloseOnLoadEnd;

  constructor(
    title: string | Node,
    {
      type,
      submit = i18n('submit.button.ok'),
      alternativeSubmit,
      cancel = false,
      loadingText,
      alternativeLoadingText,
      submitCallback,
      alternativeSubmitCallback,
      size,
      message,
      styleClass,
      shouldCloseOnLoadEnd = true,
    }: AlertDialogOptions = {},
  ) {
    super({
      ...(type && createAlertModalOptions(type)),
      size,
      submitButton: submit,
      cancelButton: cancel,
    });
    this._shouldCloseOnLoadEnd = shouldCloseOnLoadEnd;
    this.#submitCallback = submitCallback;
    this.#alternativeSubmitCallback = alternativeSubmitCallback;
    this.#loadingText = loadingText;
    this.#alternativeLoadingText = alternativeLoadingText;
    this.dialog.setAttribute('role', 'alertdialog');
    this.dialog.classList.add('alert-dialog');

    if (type) {
      this.dialog.classList.add(`alert-dialog-${type}`);
    }

    addStyleClass(this, styleClass);
    if (typeof title === 'string') {
      this.body.append(h('h2.alert-dialog--title', { innerHTML: escapeAndMarkHTML(title, false) }));
    } else {
      this.body.append(h('h2.alert-dialog--title', title));
    }
    if (message) {
      this.body.append(
        typeof message === 'string' ? h('p', { innerHTML: escapeAndMarkHTML(message, false) }) : message,
      );
    }

    if (alternativeSubmit) {
      this.footer.append(
        h('button.button', { type: 'button', onclick: () => this._onAlternativeSubmit() }, alternativeSubmit),
      );
    }
  }

  override onLoadingEnd() {
    if (this._shouldCloseOnLoadEnd) {
      this.close(true);
    }
  }

  override onSubmit() {
    this._onSubmitInternal(this.#submitCallback, this.#loadingText);
  }

  _onAlternativeSubmit() {
    this._onSubmitInternal(this.#alternativeSubmitCallback, this.#alternativeLoadingText);
  }

  _onSubmitInternal(callback?: (alertDialog: AlertDialog) => void | boolean | Promise<unknown>, loadingText?: string) {
    if (callback) {
      const result = callback(this);
      if (result instanceof Promise) {
        this.setLoading(result, loadingText);
        return;
      }
    }
    this.close(true);
  }
}

customElements.define('alert-dialog', AlertDialog);
